import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { listDailyBookings, type Booking } from "@/libs/bookingsApi";
import { listCourts, type Court } from "@/libs/facilityApi";
import {
  createZonedTimeFormatter,
  toZonedCivilDate,
} from "@/libs/zonedDateTime";

const ACTIVE_STATUSES = new Set(["confirmed", "completed"]);
const INITIAL_LOAD_ERROR = "Court occupancy is unavailable. Try again shortly.";
const REFRESH_ERROR =
  "Court occupancy could not be refreshed. The last update is still shown.";

type Occupancy =
  | { kind: "maintenance" }
  | { kind: "disabled" }
  | { kind: "in_use"; booking: Booking }
  | { kind: "open"; next: Booking | null };

interface OccupancySnapshot {
  facilityId: string;
  courts: Court[];
  bookings: Booking[];
  asOf: Date;
}

function bookingIdentity(booking: Booking): string {
  return (
    booking.display_name?.trim() || booking.event_name?.trim() || "Reserved"
  );
}

function deriveOccupancy(
  court: Court,
  bookings: Booking[],
  asOf: Date,
): Occupancy {
  if (court.status === "maintenance") return { kind: "maintenance" };
  if (court.status === "disabled") return { kind: "disabled" };

  const asOfMs = asOf.getTime();
  const courtBookings = bookings.filter(
    (booking) => booking.court_id === court.court_id,
  );
  const activeBooking = courtBookings.find((booking) => {
    if (!ACTIVE_STATUSES.has(booking.status)) return false;
    const start = new Date(booking.start_time).getTime();
    const end = new Date(booking.end_time).getTime();
    return start <= asOfMs && asOfMs < end;
  });

  if (activeBooking) return { kind: "in_use", booking: activeBooking };

  const upcoming = courtBookings
    .filter(
      (booking) =>
        booking.status === "confirmed" &&
        new Date(booking.start_time).getTime() > asOfMs,
    )
    .sort((a, b) => a.start_time.localeCompare(b.start_time));

  return { kind: "open", next: upcoming[0] ?? null };
}

export function ArenaWorkspace() {
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  const facilityTimeZone = facility?.timezone || "America/Los_Angeles";
  const facilityTimeFormatter = useMemo(
    () => createZonedTimeFormatter(facilityTimeZone),
    [facilityTimeZone],
  );
  const [snapshot, setSnapshot] = useState<OccupancySnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestGeneration = useRef(0);

  const readOccupancy = useCallback(
    async (targetFacilityId: string, timeZone: string) => {
      const asOf = new Date();
      const [courts, bookings] = await Promise.all([
        listCourts(targetFacilityId),
        listDailyBookings(targetFacilityId, toZonedCivilDate(asOf, timeZone)),
      ]);
      return { courts, bookings, asOf };
    },
    [],
  );

  useEffect(() => {
    if (!facilityId) return;

    let cancelled = false;
    const generation = ++requestGeneration.current;
    const targetFacilityId = facilityId;
    const targetTimeZone = facilityTimeZone;
    setLoading(true);
    setRefreshing(false);
    setError(null);

    void (async () => {
      try {
        const result = await readOccupancy(targetFacilityId, targetTimeZone);
        if (cancelled || generation !== requestGeneration.current) return;

        setSnapshot({ facilityId: targetFacilityId, ...result });
      } catch {
        if (cancelled || generation !== requestGeneration.current) return;

        setSnapshot((current) =>
          current?.facilityId === targetFacilityId ? current : null,
        );
        setError(INITIAL_LOAD_ERROR);
      } finally {
        if (!cancelled && generation === requestGeneration.current) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [facilityId, facilityTimeZone, readOccupancy]);

  const activeSnapshot = snapshot?.facilityId === facilityId ? snapshot : null;

  const handleRefresh = async () => {
    if (!facilityId) return;

    const targetFacilityId = facilityId;
    const targetTimeZone = facilityTimeZone;
    const hadSnapshot = activeSnapshot !== null;
    const generation = ++requestGeneration.current;
    setRefreshing(true);
    setError(null);

    try {
      const result = await readOccupancy(targetFacilityId, targetTimeZone);
      if (generation !== requestGeneration.current) return;

      setSnapshot({ facilityId: targetFacilityId, ...result });
    } catch {
      if (generation !== requestGeneration.current) return;

      setError(hadSnapshot ? REFRESH_ERROR : INITIAL_LOAD_ERROR);
    } finally {
      if (generation === requestGeneration.current) setRefreshing(false);
    }
  };

  const occupancies = useMemo(
    () =>
      activeSnapshot
        ? activeSnapshot.courts.map((court) => ({
            court,
            occupancy: deriveOccupancy(
              court,
              activeSnapshot.bookings,
              activeSnapshot.asOf,
            ),
          }))
        : [],
    [activeSnapshot],
  );
  const bookedNow = occupancies.filter(
    ({ occupancy }) => occupancy.kind === "in_use",
  ).length;
  const noBookingNow = occupancies.filter(
    ({ occupancy }) => occupancy.kind === "open",
  ).length;
  const unavailable = occupancies.filter(
    ({ occupancy }) =>
      occupancy.kind === "maintenance" || occupancy.kind === "disabled",
  ).length;

  if (facilityLoading || !facilityId) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          minHeight: "40vh",
          display: "grid",
          placeItems: "center",
          px: { xs: 2, lg: 3 },
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading arena…
        </Typography>
      </Box>
    );
  }

  const waitingForSnapshot = (loading || refreshing) && !activeSnapshot;
  const initialLoadFailed =
    !loading && !refreshing && !activeSnapshot && error !== null;

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
      >
        <Box>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
            Arena
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {facility?.name ?? "Live court occupancy"}
          </Typography>
        </Box>
        {activeSnapshot ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => void handleRefresh()}
            disabled={refreshing}
            sx={{ minHeight: 44, flexShrink: 0 }}
          >
            Refresh
          </Button>
        ) : null}
      </Stack>

      {waitingForSnapshot ? (
        <Stack role="status" aria-live="polite" spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Loading court occupancy…
          </Typography>
          <Skeleton variant="rounded" height={88} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={144} />
            ))}
          </Box>
        </Stack>
      ) : null}

      {initialLoadFailed ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void handleRefresh()}
              sx={{ minHeight: 44 }}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}

      {activeSnapshot ? (
        <>
          {error ? <Alert severity="warning">{error}</Alert> : null}

          <Paper
            component="section"
            aria-label="Court status summary"
            variant="outlined"
            sx={{ p: { xs: 1.5, sm: 2 } }}
          >
            <Box
              component="dl"
              sx={{
                m: 0,
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <SummaryValue
                label="Total courts"
                value={activeSnapshot.courts.length}
              />
              <SummaryValue label="Booked now" value={bookedNow} />
              <SummaryValue label="No booking now" value={noBookingNow} />
              <SummaryValue label="Unavailable" value={unavailable} />
            </Box>
          </Paper>

          {activeSnapshot.courts.length === 0 ? (
            <Card role="status" variant="outlined">
              <CardContent>
                <Stack spacing={1.25} sx={{ alignItems: "flex-start" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    No courts configured yet.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add courts in Courts to start monitoring live bookings.
                  </Typography>
                  <Button
                    component={Link}
                    to="/courts"
                    size="small"
                    variant="outlined"
                    sx={{ minHeight: 44 }}
                  >
                    Go to Courts
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Box
              component="ul"
              aria-label="Court occupancy"
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                  xl: "repeat(4, minmax(0, 1fr))",
                },
                gap: 1.5,
                p: 0,
                m: 0,
                listStyle: "none",
              }}
            >
              {occupancies.map(({ court, occupancy }) => (
                <CourtCard
                  key={court.court_id}
                  court={court}
                  occupancy={occupancy}
                  timeFormatter={facilityTimeFormatter}
                />
              ))}
            </Box>
          )}

          {activeSnapshot.courts.length > 0 ? (
            <Typography variant="caption" color="text.secondary">
              Occupancy is derived from today&apos;s bookings as of{" "}
              {facilityTimeFormatter.format(activeSnapshot.asOf)}.
            </Typography>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}

function SummaryValue({ label, value }: { label: string; value: number }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="dt" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="h6"
        sx={{ m: 0, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function CourtCard({
  court,
  occupancy,
  timeFormatter,
}: {
  court: Court;
  occupancy: Occupancy;
  timeFormatter: Intl.DateTimeFormat;
}) {
  const booked = occupancy.kind === "in_use";
  const unavailable =
    occupancy.kind === "maintenance" || occupancy.kind === "disabled";

  return (
    <Card
      component="li"
      variant="outlined"
      sx={{
        minWidth: 0,
        borderColor: booked ? "primary.main" : "divider",
        bgcolor: booked ? "action.selected" : "background.paper",
        opacity: unavailable ? 0.78 : 1,
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {court.name}
            </Typography>
            <StatusChip occupancy={occupancy} />
          </Stack>
          <OccupancyDetails
            occupancy={occupancy}
            timeFormatter={timeFormatter}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function StatusChip({ occupancy }: { occupancy: Occupancy }) {
  switch (occupancy.kind) {
    case "maintenance":
      return <Chip size="small" color="warning" label="Maintenance" />;
    case "disabled":
      return <Chip size="small" variant="outlined" label="Disabled" />;
    case "in_use":
      return <Chip size="small" color="primary" label="Booked now" />;
    case "open":
      return <Chip size="small" color="success" label="No booking now" />;
  }
}

function OccupancyDetails({
  occupancy,
  timeFormatter,
}: {
  occupancy: Occupancy;
  timeFormatter: Intl.DateTimeFormat;
}) {
  switch (occupancy.kind) {
    case "maintenance":
      return (
        <Typography variant="body2" color="text.secondary">
          Under maintenance.
        </Typography>
      );
    case "disabled":
      return (
        <Typography variant="body2" color="text.secondary">
          Disabled for booking.
        </Typography>
      );
    case "in_use":
      return (
        <Stack spacing={0.25}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {bookingIdentity(occupancy.booking)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Until {timeFormatter.format(new Date(occupancy.booking.end_time))}
          </Typography>
        </Stack>
      );
    case "open":
      return (
        <Stack spacing={0.25}>
          <Typography variant="body2" color="text.secondary">
            No booking now
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {occupancy.next
              ? `Next: ${timeFormatter.format(
                  new Date(occupancy.next.start_time),
                )} · ${bookingIdentity(occupancy.next)}`
              : "No upcoming bookings today"}
          </Typography>
        </Stack>
      );
  }
}
